import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { PermissionSiteClaimEditModel, RolePermissionsModel } from '@app/models/system-settings/permissions.model';
import { RoleSiteClaimPermissionsComponent } from '../role-site-claim-permissions/role-site-claim-permissions.component';

/** View model for an inner (module) panel. */
interface ModulePanel {
	name: string;
	claims: PermissionSiteClaimEditModel[];
	expanded: boolean;
}

/** View model for an outer (section) panel. */
interface SectionPanel {
	name: string;
	modules: ModulePanel[];
}

/**
 * Role permissions grouped into expandable section and module panels.
 *
 * @remarks
 * Presentational standalone component; the parent owns the `PermissionsModel` and its claim values. Each module panel
 * hosts a {@link RoleSiteClaimPermissionsComponent}. Demonstrated in Storybook as "System Settings/Role Section Permissions".
 */
@Component({
	selector: 'sb-role-section-permissions',
	standalone: true,
	imports: [MatExpansionModule, RoleSiteClaimPermissionsComponent],
	templateUrl: './role-section-permissions.component.html',
	styleUrl: './role-section-permissions.component.less',
})
export class RoleSectionPermissionsComponent implements OnChanges {
	/**
	 * Supplies the panels: `SectionModule` defines the section (outer) and module (inner) labels, and `SiteClaims`
	 * are filtered into each module. The claims' `ClaimValue`s are edited in place.
	 */
	@Input({ required: true }) permissions: RolePermissionsModel;

	/** Displays the values without allowing changes. */
	@Input() readOnly = false;

	/** Emits (without a payload) after one or more `ClaimValue`s have changed. */
	@Output() changed = new EventEmitter<void>();

	/** Panels derived from `permissions`. */
	sections: SectionPanel[] = [];

	/**
	 * Rebuilds the section and module panels whenever `permissions` is replaced, keeping the modules that were expanded.
	 *
	 * @remarks
	 * The parent form can hand back a different (cloned) `permissions` object after a save or reset, which must not
	 * collapse the panels the user has open.
	 */
	ngOnChanges(): void {
		// SectionModule is deserialized JSON at runtime, so it is a plain object rather than a Map.
		const sectionModule = (this.permissions?.SectionModule ?? {});
		const claims = (this.permissions?.SiteClaims ?? []);
		const expandedModules = new Set(
			this.sections.flatMap((section) => section.modules.filter((module) => module.expanded).map((module) => `${section.name}\n${module.name}`))
		);

		this.sections = Object.entries(sectionModule).map(([sectionName, moduleNames]) => ({
			name: sectionName,
			modules: moduleNames.map((moduleName) => ({
				name: moduleName,
				claims: claims.filter((claim) => claim.SectionDisplayName === sectionName && claim.ModuleDisplayName === moduleName),
				expanded: expandedModules.has(`${sectionName}\n${moduleName}`),
			})),
		}));
	}

	/**
	 * Expands or collapses every module panel within one section panel.
	 *
	 * @param section - The section whose module panels should change.
	 * @param expanded - `true` to expand, `false` to collapse.
	 */
	setAllExpanded(section: SectionPanel, expanded: boolean): void {
		section.modules.forEach((module) => (module.expanded = expanded));
	}
}
